import React from "react";
import PropTypes from "prop-types";
import {
  Card,
  Box,
  Typography,
  Stack,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Calendar,
  Clock,
  Share2,
  ArrowLeft,
  Star,
  CheckCircle2,
  BookOpen,
} from "lucide-react";
import { useHistory } from "react-router-dom";
import { colors, borderRadiusPx, customShadows } from "../../../../../theme";
import { Path } from "../../../../../static/Constants";
import { CommonButton, CommonChip, CommonBreadcrumbs } from "../../../../Components/ui";

const isFeaturedArticle = (item) => {
  if (!item) return false;
  return (
    item.featured === true ||
    item.featured === 1 ||
    item.featured === "1" ||
    item.featured === "true" ||
    item.isFeatured === true ||
    item.isFeatured === 1 ||
    item.isFeatured === "1" ||
    item.isFeatured === "true"
  );
};

const NewsDetailsHeader = ({
  article,
  onShare,
  readingTime = "2 min read",
  formatTimeAgo,
  formatPublishDate,
}) => {
  const history = useHistory();
  if (!article) return null;

  const {
    title,
    updatedOn,
    createdOn,
  } = article;

  const isFeatured = isFeaturedArticle(article);
  const displayDate = updatedOn || createdOn;

  return (
    <Box sx={{ mb: 4 }}>
      {/* Breadcrumb Navigation */}
      <CommonBreadcrumbs
        items={[
          { label: "Home", path: Path.root },
          { label: "News & Updates", path: Path.news },
          { label: title, truncate: true },
        ]}
      />


      {/* Header Container Card */}
      <Card
        sx={{
          p: { xs: 3, sm: 4 },
          borderRadius: borderRadiusPx.xl,
          backgroundColor: colors.dashboard.cardBg,
          border: `1px solid ${colors.dashboard.cardBorder}`,
          boxShadow: customShadows.cardFlat,
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Top Accent Gradient Stripe */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 4,
            background: colors.gradients.primary,
          }}
        />

        {/* Top Badges & Actions Row */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1.5,
            mb: 2,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ gap: 1 }}>
            {isFeatured ? (
              <CommonChip
                label="Featured Story"
                startIcon={<Star size={12} fill={colors.amber[500]} color={colors.amber[500]} />}
                size="small"
                sx={{
                  bgcolor: colors.amber[50],
                  color: colors.amber[800],
                  fontWeight: 700,
                  border: `1px solid ${colors.amber[200]}`,
                }}
              />
            ) : (
              <CommonChip
                label="Official Announcement"
                startIcon={<CheckCircle2 size={12} color={colors.primary.main} />}
                size="small"
                sx={{
                  bgcolor: colors.blue[50],
                  color: colors.primary.main,
                  fontWeight: 700,
                  border: `1px solid ${colors.blue[200]}`,
                }}
              />
            )}

            {/* <CommonChip
              label="Ecosystem"
              size="small"
              sx={{
                bgcolor: colors.slate[100],
                color: colors.slate[700],
                fontWeight: 600,
              }}
            /> */}
          </Stack>

          {/* Quick Back & Share Actions */}
          <Stack direction="row" spacing={1} alignItems="center">
            <CommonButton
              variant="outline"
              size="small"
              startIcon={<ArrowLeft size={14} />}
              onClick={() => history.push(Path.news)}
            >
              All News
            </CommonButton>

            <Tooltip title="Share this article link">
              <IconButton
                size="small"
                onClick={onShare}
                sx={{
                  bgcolor: colors.slate[50],
                  color: colors.slate[600],
                  border: `1px solid ${colors.slate[200]}`,
                  "&:hover": {
                    bgcolor: colors.blue[50],
                    color: colors.primary.main,
                    borderColor: colors.blue[200],
                  },
                }}
              >
                <Share2 size={16} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        {/* Main Article Title */}
        <Typography
          variant="h3"
          sx={{
            fontWeight: 900,
            color: colors.slate[900],
            letterSpacing: "-0.5px",
            fontSize: { xs: "1.65rem", sm: "2.2rem", md: "2.5rem" },
            lineHeight: 1.25,
            mb: 2.5,
            textAlign:'left'
          }}
        >
          {title}
        </Typography>

        {/* Metadata Details Row */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2.5,
            pt: 2,
            borderTop: `1px solid ${colors.dashboard.tableBorder}`,
          }}
        >
          {/* Author / Source */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: borderRadiusPx.circle,
                bgcolor: colors.blue[50],
                color: colors.primary.main,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "0.75rem",
                fontWeight: 800,
              }}
            >
              AB
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 700, color: colors.slate[800] }}>
              Autobet Team
            </Typography>
          </Box>

          {/* Published Date */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, color: colors.slate[500] }}>
            <Calendar size={14} color={colors.slate[400]} />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {formatPublishDate(displayDate)}
            </Typography>
          </Box>

          {/* Time Ago */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, color: colors.slate[500] }}>
            <Clock size={14} color={colors.slate[400]} />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {formatTimeAgo(displayDate)}
            </Typography>
          </Box>

          {/* Reading Time */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, color: colors.slate[500] }}>
            <BookOpen size={14} color={colors.slate[400]} />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {readingTime}
            </Typography>
          </Box>
        </Box>
      </Card>
    </Box>
  );
};

NewsDetailsHeader.propTypes = {
  article: PropTypes.object,
  onShare: PropTypes.func.isRequired,
  readingTime: PropTypes.string,
  formatTimeAgo: PropTypes.func.isRequired,
  formatPublishDate: PropTypes.func.isRequired,
};

export default NewsDetailsHeader;
